import { iterateValues } from "../../../../../../Common/IterableUtils";
import { isNotNullOrUndefined } from "../../../../../../Common/TypingUtils";
import { ModelActionType } from "../../ModelStoring/Store/modelActions";
import { FullFormAction } from "../../Store/FullFormAction";
import { FullFormState } from "../../Store/FullFormState";
import { AbsoluteModelPath } from "../../../../../../Common/ModelPath/ModelPath";
import {
    CandyPopdProtocolState,
    PopdResultCode,
} from "../../../../../../Common/CandyApiClient/Types/ErrorLog/CandyPopdProtocol";
import { ErrorLog, ErrorLogItems } from "../Utils/Models";
import { getErrorItemBindingPaths } from "../Utils/Utils";
import { AttachmentErrorLog } from "../../../../../../Common/CandyApiClient/Types/ErrorLog/AttachmentErrorLog";

import { ErrorLogActionsType } from "./ErrorLogActions";

export const defaultErrorLogState: ErrorLogState = {
    modelChangedAfterCheck: false,
    form: { errorsTotalCount: 0, warningsTotalCount: 0, successesTotalCount: 0, errorGroupInfos: [] },
};

export interface ErrorLogState {
    modelChangedAfterCheck: boolean;
    form: ErrorLog;
    expert?: ErrorLog;
    popdProtocol?: CandyPopdProtocolState;
    attachmentErrorLogs?: AttachmentErrorLog;
}

function updateErrorItemsTouchedState(
    errorItems: undefined | Readonly<ErrorLogItems>,
    touchedPaths: Iterable<AbsoluteModelPath>
): undefined | ErrorLogItems {
    if (errorItems == undefined) {
        return errorItems;
    }
    const touchedBindingPaths = new Set(Iterator.from(touchedPaths).map(x => x.toLegacyPath()));
    let nextErrorItems = errorItems;
    for (const item of Iterator.from(iterateValues(errorItems)).filter(isNotNullOrUndefined)) {
        const itemBindingPaths = Iterator.from(getErrorItemBindingPaths(item));
        if (itemBindingPaths.some(x => touchedBindingPaths.has(x))) {
            nextErrorItems = {
                ...nextErrorItems,
                [item.id]: { ...item, touched: true },
            };
        }
    }
    return nextErrorItems;
}

export function errorLogModelReducer(state: FullFormState, action: FullFormAction): FullFormState {
    let nextState = state;
    if (action.type === ErrorLogActionsType.UpdateModelState) {
        nextState = {
            ...nextState,
            errorLog: {
                ...nextState.errorLog,
                modelChangedAfterCheck: action.payload.modelChanged,
            },
        };
    }
    if (action.type === ModelActionType.SetValues) {
        if (!nextState.errorLog.modelChangedAfterCheck) {
            nextState = {
                ...nextState,
                errorLog: {
                    ...nextState.errorLog,
                    modelChangedAfterCheck: true,
                },
            };
        }
        if (nextState.errorLog.form.errors != undefined && nextState.errorLog.form.warnings != undefined) {
            const pathWithChangedValue = Iterator.from(action.payload.values.iterateEntries())
                .filter(([_, update]) => "value" in update)
                .map(([path]) => path)
                .toArray();
            const deletedPaths = action.payload.deleted;
            const touchedBindingPaths = [...pathWithChangedValue, ...deletedPaths];
            const nextErrors = updateErrorItemsTouchedState(nextState.errorLog.form.errors, touchedBindingPaths);
            const nextWarning = updateErrorItemsTouchedState(nextState.errorLog.form.warnings, touchedBindingPaths);
            if (nextState.errorLog.form.errors !== nextErrors || nextState.errorLog.form.warnings !== nextWarning) {
                nextState = {
                    ...nextState,
                    errorLog: {
                        ...nextState.errorLog,
                        form: {
                            ...nextState.errorLog.form,
                            errors: nextErrors,
                            warnings: nextWarning,
                        },
                    },
                };
            }
        }
    }
    return nextState;
}

export function errorLogReducer(state: ErrorLogState, action: FullFormAction): ErrorLogState {
    switch (action.type) {
        case ErrorLogActionsType.requestErrorlog:
            return {
                ...state,
                modelChangedAfterCheck: false,
                form: {
                    ...state.form,

                    isChecking: true,
                },
                popdProtocol: undefined,
            };
        case ErrorLogActionsType.receivedErrorLog: {
            const { errorsTotalCount = 0, warningsTotalCount = 0 } = action.payload;
            const counterEnabled = state.form.counterEnabled || Boolean(errorsTotalCount + warningsTotalCount);
            return {
                ...state,
                form: {
                    isChecking: false,
                    errorLogWasRequested: true,
                    counterEnabled: counterEnabled,
                    ...action.payload,
                },
            };
        }
        case ErrorLogActionsType.requestFailed:
            return {
                ...state,
                form: {
                    ...defaultErrorLogState.form,
                    requestFailed: true,
                    isChecking: false,
                },
                popdProtocol: undefined,
            };
        case ErrorLogActionsType.requestExpertErrorlog: {
            return {
                ...state,
                expert: {
                    ...state.form,
                    isChecking: true,
                },
            };
        }
        case ErrorLogActionsType.receivedExpertErrorLog: {
            return {
                ...state,
                expert: action.payload,
            };
        }
        case ErrorLogActionsType.requestExpertErrorLogFailed:
            return {
                ...state,
                expert: {
                    errorsTotalCount: 0,
                    warningsTotalCount: 0,
                    successesTotalCount: 0,
                    errorGroupInfos: [],
                    requestFailed: true,
                },
            };
        case ErrorLogActionsType.requestPopdProtocol:
            return {
                ...state,
                popdProtocol: {
                    ...state.popdProtocol,
                    isChecking: true,
                },
            };
        case ErrorLogActionsType.receivedPopdProtocol: {
            return {
                ...state,
                popdProtocol: {
                    ...action.payload,
                    isChecking: false,
                },
            };
        }
        case ErrorLogActionsType.requestPopdProtocolFailed:
            return {
                ...state,
                popdProtocol: {
                    resultCode: PopdResultCode.Error,
                    isChecking: false,
                },
            };
        case ErrorLogActionsType.setAttachmentErrors:
            return {
                ...state,
                attachmentErrorLogs: action.payload,
            };
        case ErrorLogActionsType.clearAttachmentErrors:
            return {
                ...state,
                attachmentErrorLogs: undefined,
            };
        default:
            return state;
    }
}
